import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, arrayRemove, getDoc } from 'firebase/firestore';
import { BackButton } from '../components/ui';
import { BottomNavigation } from '../components/BottomNavigation';

const PRIMARY_BROWN = '#C97B53';
const ERROR_RED = '#F44336';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

export default function Favorites() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('products');
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [favoriteShops, setFavoriteShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    // Fetch user's favorites from their user document
    async function fetchFavorites() {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error('User not found');
        const userData = userSnap.data();
        const favProducts = userData?.favoriteProducts || [];
        const favShops = userData?.favorited_shops || [];
        // Fetch product/shop details using getDoc for each
        const productDocs = await Promise.all(
          favProducts.map(async pid => {
            const prodRef = doc(db, 'products', pid);
            const prodSnap = await getDoc(prodRef);
            return prodSnap.exists() ? { id: prodSnap.id, ...prodSnap.data() } : null;
          })
        );
        const shopDocs = await Promise.all(
          favShops.map(async sid => {
            const shopRef = doc(db, 'shops', sid);
            const shopSnap = await getDoc(shopRef);
            return shopSnap.exists() ? { id: shopSnap.id, ...shopSnap.data() } : null;
          })
        );
        setFavoriteProducts(productDocs.filter(Boolean));
        setFavoriteShops(shopDocs.filter(Boolean));
      } catch (err) {
        setError('Failed to load favorites.');
      } finally {
        setLoading(false);
      }
    }
    fetchFavorites();
  }, [currentUser]);

  const handleUnfavorite = async (type, id) => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    try {
      await updateDoc(userRef, {
        [type === 'product' ? 'favoriteProducts' : 'favoriteShops']: arrayRemove(id)
      });
      if (type === 'product') setFavoriteProducts(favoriteProducts.filter(p => p.id !== id));
      else setFavoriteShops(favoriteShops.filter(s => s.id !== id));
    } catch (err) {
      setError('Failed to update favorites.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: 96 }}>
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: NEUTRAL_WHITE, borderBottom: '1px solid #eee', padding: '12px 0 0 0', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px 12px 20px' }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
          <h1 style={{ ...headingFont, fontSize: 22, color: NEUTRAL_TEXT, margin: 0 }}>Favorites</h1>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', background: NEUTRAL_WHITE, borderRadius: 20, boxShadow: '0 2px 8px #0001', padding: '28px 24px' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => setTab('products')}
            style={{
              background: 'transparent',
              color: tab === 'products' ? PRIMARY_BROWN : ACCENT_GRAY,
              border: 'none',
              borderRadius: 10,
              padding: '10px 32px',
              fontSize: 16,
              fontWeight: tab === 'products' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              ...bodyFont
            }}
          >
            Products
          </button>
          <button
            onClick={() => setTab('shops')}
            style={{
              background: 'transparent',
              color: tab === 'shops' ? PRIMARY_BROWN : ACCENT_GRAY,
              border: 'none',
              borderRadius: 10,
              padding: '10px 32px',
              fontSize: 16,
              fontWeight: tab === 'shops' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              ...bodyFont
            }}
          >
            Shops
          </button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>Loading favorites...</div>
        ) : error ? (
          <div style={{ color: ERROR_RED, textAlign: 'center' }}>{error}</div>
        ) : (
          <>
            {tab === 'products' ? (
              favoriteProducts.length === 0 ? (
                <div style={{ color: ACCENT_GRAY, textAlign: 'center', fontSize: 16 }}>You haven't favorited any products yet. Start exploring!</div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 16,
                  width: '100%',
                  background: '#fff',
                  borderRadius: 16,
                  padding: 8,
                  minHeight: 320
                }}>
                  {favoriteProducts.map(product => (
                    <div
                      key={product.id}
                      style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px #0001', padding: 18, cursor: 'pointer', position: 'relative' }}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1', marginBottom: 12 }}>
                        <img src={product.imageUrl || 'https://via.placeholder.com/120'} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                        <button
                          onClick={e => { e.stopPropagation(); handleUnfavorite('product', product.id); }}
                          style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: ERROR_RED, fontSize: 22, cursor: 'pointer', zIndex: 2 }}
                          aria-label="Remove from favorites"
                        >
                          ♥
                        </button>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 4 }}>{product.name}</div>
                      <div style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 4 }}>{product.shopName}</div>
                      <div style={{ color: PRIMARY_BROWN, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>${product.price}</div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              favoriteShops.length === 0 ? (
                <div style={{ color: ACCENT_GRAY, textAlign: 'center', fontSize: 16 }}>You haven't favorited any shops yet. Start exploring!</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
                  {favoriteShops.map(shop => (
                    <div
                      key={shop.id}
                      style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px #0001', padding: 18, cursor: 'pointer', position: 'relative' }}
                      onClick={() => navigate(`/storefront/${shop.id}`)}
                    >
                      <img src={shop.profileImage || 'https://via.placeholder.com/120'} alt={shop.name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }} />
                      <div style={{ fontWeight: 700, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 4 }}>{shop.name}</div>
                      <div style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 4 }}>Rating: {shop.rating || 'N/A'}</div>
                      <button
                        onClick={e => { e.stopPropagation(); handleUnfavorite('shop', shop.id); }}
                        style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: ERROR_RED, fontSize: 22, cursor: 'pointer' }}
                        aria-label="Remove from favorites"
                      >
                        ♥
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
      <BottomNavigation />
    </div>
  );
} 